import { getIn, useFormikContext } from 'formik';
import * as React from 'react';

import { Select, SelectContent, SelectFieldTrigger, SelectValue } from '@/design-system/components/ui';

export interface FormikSelectProps {
  name: string;
  label?: string;
  children: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  parseValue?: (val: string) => unknown;
}

const FormikSelect = React.forwardRef<HTMLButtonElement, FormikSelectProps>(
  ({ name, label, children, placeholder, disabled, className, parseValue }, ref) => {
    const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext();

    const value = getIn(values, name);
    const error = getIn(errors, name);
    const fieldTouched = getIn(touched, name);

    const hasError = Boolean(fieldTouched && error);
    const errorMessage = fieldTouched && typeof error === 'string' ? error : undefined;

    const handleValueChange = React.useCallback(
      (val: string) => {
        const parsed = parseValue ? parseValue(val) : val;
        setFieldValue(name, parsed);
      },
      [name, parseValue, setFieldValue],
    );

    const handleBlur = React.useCallback(() => {
      setFieldTouched(name, true);
    }, [name, setFieldTouched]);

    return (
      <Select value={value != null ? String(value) : undefined} onValueChange={handleValueChange} disabled={disabled}>
        <SelectFieldTrigger
          ref={ref}
          className={className}
          label={label}
          error={hasError}
          errorMessage={errorMessage}
          disabled={disabled}
          onBlur={handleBlur}
        >
          <SelectValue placeholder={placeholder} />
        </SelectFieldTrigger>
        <SelectContent>{children}</SelectContent>
      </Select>
    );
  },
);

FormikSelect.displayName = 'FormikSelect';

export { FormikSelect };
